// pages/UserAdmin/crm/LembretesContato.js — a seção "Lembretes" da janela do
// contato (out/26, CRM moderno).
//
// Até aqui era um aviso "Lembretes chegam na próxima atualização", com as
// duas coisas já prontas em outro lugar: o retorno na Agenda (lembrete do
// CRM) e o disparo agendado de template. A seção junta as duas no contato:
//
//   - o que está marcado com ele na Agenda — lembretes do CRM e consultas,
//     desde a semana passada (o retorno atrasado aparece) até uns dois
//     meses à frente; o clique abre o compromisso na Agenda;
//   - "Lembrar de retornar" abre o MESMO painel da Agenda, já com o contato
//     e um título — o compromisso se grava num lugar só, com as regras dela
//     (responsável, conflito de horário, aviso no sino);
//   - "Agendar envio" abre os disparos agendados só pra ele (mesma rota e
//     mesmas travas: opt-in, tema, teto).
//
// O disparo agendado NÃO entra na lista: a API devolve o disparo com a
// contagem de contatos, não com quais são, e esta tela não muda o backend.
// Depois de agendar, a seção confirma o que ficou marcado e diz onde
// acompanhar ("Disparos agendados", na tela de Contatos).
//
// Datas: a Agenda fala em data e hora LOCAIS do fuso da operação ("hoje"
// vem do servidor em /agenda/config) — o relógio do navegador não entra.

import { useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { FiCalendar, FiCheck, FiChevronRight, FiSend } from 'react-icons/fi';
import api from '../../../services/Api';
import PainelCompromisso from '../../agenda/PainelCompromisso';
import { add, DC, MES_C } from '../../agenda/datas';
import { TIPOS, cor, tomStatus, umTipo } from '../../agenda/estilo';
import LembretesDialog from './LembretesDialog';
import { OUTFIT, formatarFone, rotulo } from './theme';

// A API aceita no máximo 62 dias por pedido: uma semana pra trás, o resto
// pra frente.
const DIAS_ATRAS = 7;
const DIAS_FRENTE = 55;
// Mais que isso vira "Ver todos na Agenda" — a janela do contato não é a
// Agenda.
const MAX_ITENS = 4;

const foco = { '&:focus-visible': { outline: '2px solid var(--ch-green)', outlineOffset: 2 } };

function quandoTxt(ev, hoje) {
  if (ev.data === hoje) return `Hoje · ${ev.ini}`;
  if (ev.data === add(hoje, 1)) return `Amanhã · ${ev.ini}`;
  if (ev.data === add(hoje, -1)) return `Ontem · ${ev.ini}`;
  const d = new Date(`${ev.data}T12:00:00Z`);
  return `${DC[d.getUTCDay()].toLowerCase()} · ${ev.ini}`;
}

function formatarQuando(iso) {
  try {
    return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
      .replace('.', '').replace(',', ' ·');
  } catch {
    return '';
  }
}

function Acao({ icone, children, onClick, principal }) {
  return (
    <Box component="button" type="button" onClick={onClick}
      sx={{
        // Base = o próprio texto: se os dois não cabem lado a lado, o
        // segundo desce pra linha de baixo em vez de espremer o rótulo.
        ...foco, flex: '1 1 auto', height: 40, px: '14px', borderRadius: '12px', cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
        fontFamily: OUTFIT, fontSize: 14.5, fontWeight: 600, whiteSpace: 'nowrap',
        border: `1px solid ${principal ? 'var(--ch-green-b)' : 'var(--ch-bdr)'}`,
        bgcolor: principal ? 'var(--ch-green-bg)' : 'var(--ch-card)',
        color: principal ? 'var(--ch-green-d)' : 'var(--ch-text2)',
        transition: 'border-color .15s, color .15s',
        '&:hover': { borderColor: principal ? 'var(--ch-green)' : 'var(--ch-bdr2)', color: principal ? 'var(--ch-green-d)' : 'var(--ch-text)' },
      }}>
      <Box component="span" sx={{ display: 'flex', flexShrink: 0 }}>{icone}</Box>
      {children}
    </Box>
  );
}

/**
 * @param contato   o contato aberto
 * @param token     JWT (o LembretesDialog ainda recebe por prop)
 * @param onAbrirAgenda (url) => void — sai pra Agenda (quem chama grava o
 *                  que estiver pendente na janela antes de navegar)
 */
export default function LembretesContato({ contato, token, onAbrirAgenda }) {
  const [config, setConfig] = useState(null);
  const [eventos, setEventos] = useState(null);   // null = carregando
  const [erro, setErro] = useState(false);
  const [painel, setPainel] = useState(false);
  const [envio, setEnvio] = useState(false);
  const [agendado, setAgendado] = useState(null);  // disparo recém-agendado
  const [marcado, setMarcado] = useState(null);     // compromisso recém-criado

  const carregar = useCallback(async () => {
    setErro(false);
    setEventos(null);
    try {
      const cfg = config || (await api.get('/agenda/config')).data;
      if (!config) setConfig(cfg);
      const { data } = await api.get('/agenda/events', {
        params: { de: add(cfg.hoje, -DIAS_ATRAS), ate: add(cfg.hoje, DIAS_FRENTE) },
      });
      const doContato = (data?.eventos || []).filter((ev) => (
        ev.contato?.id === contato.id
        && (ev.tipo === 'consulta' || ev.tipo === 'lembrete')
        && ev.status !== 'cancelada'
        // Do passado, só o que ainda pede ação: o lembrete atrasado. Consulta
        // que já aconteceu e lembrete concluído ficam na Agenda.
        && (ev.data >= cfg.hoje || ev.status === 'atrasado')
      ));
      setEventos(doContato);
    } catch {
      setErro(true);
      setEventos([]);
    }
  }, [config, contato.id]);

  useEffect(() => {
    setAgendado(null);
    setMarcado(null);
    carregar();
    // Só ao trocar de contato; `carregar` muda quando o config chega.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contato.id]);

  const hoje = config?.hoje;
  const lista = eventos || [];
  const visiveis = lista.slice(0, MAX_ITENS);
  const nomeContato = contato.name || contato.phone_number;

  return (
    <Box component="section" aria-labelledby="crm-lembretes-titulo">
      <Box id="crm-lembretes-titulo" sx={{ ...rotulo, mb: '12px' }}>Lembretes</Box>

      {eventos === null ? (
        <Box aria-busy="true" sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {[0, 1].map((i) => (
            <Box key={i} sx={{ height: 58, borderRadius: '14px', bgcolor: 'var(--ch-inset)', opacity: 1 - i * 0.35 }} />
          ))}
        </Box>
      ) : erro ? (
        <Box role="alert" sx={{ p: '14px 16px', borderRadius: '14px', border: '1px dashed var(--ch-bdr2)', fontSize: 14, color: 'var(--ch-text2)', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <Box component="span" sx={{ flex: 1, minWidth: 160 }}>Não foi possível carregar a Agenda deste contato.</Box>
          <Box component="button" type="button" onClick={carregar}
            sx={{ ...foco, border: 0, bgcolor: 'transparent', p: 0, cursor: 'pointer', fontFamily: OUTFIT, fontSize: 14, fontWeight: 600, color: 'var(--ch-green-d)' }}>
            Tentar de novo
          </Box>
        </Box>
      ) : lista.length === 0 ? (
        <Box sx={{ p: '14px 16px', borderRadius: '14px', border: '1px dashed var(--ch-bdr2)', fontSize: 14, lineHeight: 1.5, color: 'var(--cx-cap)' }}>
          Nada marcado com {nomeContato} na Agenda. Marque quando retornar ou agende uma mensagem.
        </Box>
      ) : (
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {visiveis.map((ev) => {
            const tipo = cor(TIPOS[ev.tipo]?.c || 'green');
            const st = tomStatus(ev.status_tom);
            const d = new Date(`${ev.data}T12:00:00Z`);
            return (
              <Box component="li" key={ev.id}>
                <Box component="button" type="button"
                  onClick={() => onAbrirAgenda?.(`/agenda?evento=${ev.id}&data=${ev.data}`)}
                  aria-label={`${umTipo(ev.tipo, config?.nome_consulta)}: ${ev.titulo}, ${quandoTxt(ev, hoje)}, ${ev.status_txt}. Abrir na Agenda`}
                  sx={{
                    ...foco, width: '100%', textAlign: 'left', cursor: 'pointer', fontFamily: OUTFIT,
                    display: 'flex', alignItems: 'center', gap: '12px', p: '10px 12px 10px 10px',
                    borderRadius: '14px', border: '1px solid var(--ch-bdr)', bgcolor: 'var(--ch-surf)', color: 'var(--ch-text)',
                    transition: 'border-color .15s',
                    '&:hover': { borderColor: 'var(--ch-bdr2)' },
                    '&:hover .cx-seta': { color: 'var(--ch-text)' },
                  }}>
                  {/* A folhinha: dia e mês, na cor do tipo (a mesma da Agenda). */}
                  <Box component="span" aria-hidden sx={{
                    width: 42, flexShrink: 0, borderRadius: '10px', overflow: 'hidden', textAlign: 'center',
                    border: `1px solid ${tipo.b}`, bgcolor: 'var(--ch-card)',
                  }}>
                    <Box component="span" sx={{ display: 'block', py: '2px', bgcolor: tipo.bg, color: tipo.d, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                      {MES_C[d.getUTCMonth()]}
                    </Box>
                    <Box component="span" sx={{ display: 'block', py: '2px', fontFamily: OUTFIT, fontSize: 17, fontWeight: 700, lineHeight: 1.25, color: 'var(--ch-text)', fontVariantNumeric: 'tabular-nums' }}>
                      {d.getUTCDate()}
                    </Box>
                  </Box>
                  <Box component="span" sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <Box component="span" sx={{ fontSize: 14.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {ev.tipo === 'consulta' ? umTipo('consulta', config?.nome_consulta) : ev.titulo}
                    </Box>
                    <Box component="span" sx={{ fontSize: 13, color: 'var(--cx-cap)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontVariantNumeric: 'tabular-nums' }}>
                      {quandoTxt(ev, hoje)}{ev.responsavel ? ` · ${ev.responsavel.nome}` : ''}
                    </Box>
                  </Box>
                  <Box component="span" sx={{
                    flexShrink: 0, height: 24, px: '9px', borderRadius: '999px', display: 'inline-flex', alignItems: 'center',
                    fontSize: 12, fontWeight: 600, color: st.cor, bgcolor: st.bg, border: `1px solid ${st.b}`, whiteSpace: 'nowrap',
                  }}>
                    {ev.status_txt}
                  </Box>
                  <Box component="span" className="cx-seta" aria-hidden sx={{ display: 'flex', color: 'var(--cx-cap)', flexShrink: 0, transition: 'color .15s' }}>
                    <FiChevronRight size={16} />
                  </Box>
                </Box>
              </Box>
            );
          })}
          {lista.length > MAX_ITENS && (
            <Box component="li">
              <Box component="button" type="button" onClick={() => onAbrirAgenda?.(`/agenda?data=${hoje}`)}
                sx={{ ...foco, border: 0, bgcolor: 'transparent', p: '2px 0', cursor: 'pointer', fontFamily: OUTFIT, fontSize: 14, fontWeight: 600, color: 'var(--ch-green-d)' }}>
                Ver os {lista.length} na Agenda
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* O que acabou de ser feito por aqui: confirma e diz onde acompanhar. */}
      {(marcado || agendado) && (
        <Box role="status" sx={{
          mt: '10px', p: '10px 12px', borderRadius: '12px', display: 'flex', alignItems: 'flex-start', gap: '8px',
          bgcolor: 'var(--ch-green-bg)', border: '1px solid var(--ch-green-b)', color: 'var(--ch-green-d)', fontSize: 14, lineHeight: 1.45,
        }}>
          <Box component="span" sx={{ display: 'flex', pt: '2px', flexShrink: 0 }}><FiCheck size={15} strokeWidth={2.6} /></Box>
          <Box component="span">
            {marcado && <>Marcado na Agenda: <b>{marcado}</b>.</>}
            {marcado && agendado && ' '}
            {agendado && (
              <>
                Envio agendado: <b>{agendado.template_name}</b>, {formatarQuando(agendado.scheduled_at)}.
                {' '}Acompanhe em “Disparos agendados”, na tela de Contatos.
              </>
            )}
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', mt: '12px' }}>
        <Acao principal icone={<FiCalendar size={15} />} onClick={() => setPainel(true)}>Lembrar de retornar</Acao>
        <Acao icone={<FiSend size={15} />} onClick={() => setEnvio(true)}>Agendar envio</Acao>
      </Box>

      {/* O painel da Agenda, por cima da janela do contato (z-index do
          Dialog é 1300). Abre amanhã às 9h — "retornar" é depois. */}
      {config && (
        <PainelCompromisso
          aberto={painel}
          modo="novo"
          config={config}
          camada={1400}
          inicial={{
            tipo: 'lembrete',
            data: add(config.hoje, 1),
            ini: '09:00',
            titulo: `Retornar para ${nomeContato}`,
            contato: { id: contato.id, nome: nomeContato, fone: formatarFone(contato.phone_number) },
          }}
          onFechar={() => setPainel(false)}
          onSalvo={(ev) => {
            setMarcado(`${ev?.titulo || 'Retorno'}, ${ev ? quandoTxt(ev, config.hoje).toLowerCase() : ''}`);
            carregar();
          }}
        />
      )}

      <LembretesDialog
        open={envio}
        onClose={() => setEnvio(false)}
        token={token}
        contato={contato}
        onAgendado={setAgendado}
      />
    </Box>
  );
}
